import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { SmartLinkStatsSeriesPoint } from 'fym-shared/src/smartlinks/stats';
import { TypedMemo } from '../../common/types';
import { useChartTokens } from '../../music24/theme/useChartTokens';
import { Panel, PanelBody, PanelHead } from '../../music24/components/primitives/Panel';
import { mediaStatsMessages } from './media/mediaStatsMessages';
import styles from './SMLClicksChart.module.scss';

interface SMLClicksChartProps {
  series: SmartLinkStatsSeriesPoint[];
  days: number;
  /**
   * What to draw instead of "nothing yet" when the caller knows why there is
   * nothing — an unpublished page has a cause and a cure, and the plain copy
   * gives neither.
   */
  empty?: React.ReactNode;
}

const SMLClicksChartComponent: React.FC<SMLClicksChartProps> = ({ series, days, empty }) => {
  const intl = useIntl();
  const tokens = useChartTokens();

  const hasPlays = useMemo(() => series.some((point) => (point.mediaPlays ?? 0) > 0), [series]);
  const hasData = useMemo(
    () => hasPlays || series.some((point) => point.views > 0 || point.clicks > 0),
    [hasPlays, series]
  );

  return (
    <Panel className={styles.panel}>
      <PanelHead
        title={intl.formatMessage({ id: 'app.sml.stats.chart.title', defaultMessage: 'Views and clicks' })}
        right={intl.formatMessage({ id: 'app.sml.stats.chart.range', defaultMessage: 'last {days} days' }, { days })}
      />
      <PanelBody>
        {hasData ? (
          <div className={styles.chart}>
            <ResponsiveContainer width="100%" height="100%">
              {/* Bars, not a smoothed line: these are counts for whole days.
                A curve draws values between two days that never existed. */}
              <BarChart data={series} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barGap={2}>
                <CartesianGrid stroke={tokens.grid} vertical={false} />
                <XAxis dataKey="date" stroke={tokens.axis} tickLine={false} fontSize={11} minTickGap={16} />
                <YAxis
                  stroke={tokens.axis}
                  tickLine={false}
                  axisLine={false}
                  fontSize={11}
                  width={36}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: tokens.cursor, opacity: 0.15 }}
                  contentStyle={{ background: tokens.tooltipBg, border: `1px solid ${tokens.line}`, borderRadius: 10 }}
                  labelStyle={{ color: tokens.tx2 }}
                  itemStyle={{ color: tokens.tooltipTx }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: tokens.tx2 }} iconType="circle" iconSize={8} />
                {/* Side by side rather than stacked: a click happens within a
                  view, so stacking them would overstate the day's total. */}
                <Bar
                  dataKey="views"
                  fill={tokens.brandLift}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={26}
                  name={intl.formatMessage({ id: 'app.sml.stats.views', defaultMessage: 'Page views' })}
                />
                <Bar
                  dataKey="clicks"
                  fill={tokens.pos}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={26}
                  name={intl.formatMessage({ id: 'app.sml.stats.clicks', defaultMessage: 'Link clicks' })}
                />
                {/* The plays of the Media blocks (design c1), only when the window has some. */}
                {hasPlays && (
                  <Bar
                    dataKey="mediaPlays"
                    fill={tokens.brand}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={26}
                    name={intl.formatMessage(mediaStatsMessages.playsLine)}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          (empty ?? (
            <div className={styles.empty}>
              {intl.formatMessage({
                id: 'app.sml.stats.chart.empty',
                defaultMessage: 'Nothing yet. Numbers appear once fans start opening the page.',
              })}
            </div>
          ))
        )}
      </PanelBody>
    </Panel>
  );
};

export const SMLClicksChart = TypedMemo(SMLClicksChartComponent);
